/**
 * @file Decides when a call that looks alive has stopped carrying media (#2223 AC13).
 *
 * Found on a real Android Chrome Wi-Fi→4G switch: LiveKit resumed over the
 * new network, the server dropped every candidate on the new path ("ufrag
 * doesn't match"), and the client stayed `connected` -- no reconnecting pill,
 * no fallback -- while no audio went either way for 67 s. LiveKit's own safety
 * nets did not fire: its reconcile check trusts the transport's reported
 * state, and its "quality lost" trigger needs the server to report LOST.
 *
 * The signal used here is the SFU's answer to what we send: the bytes the
 * *publisher* connection receives (RTCP receiver reports and congestion
 * feedback). They keep coming while the mic is published at all -- a muted
 * mic still sends Opus silence frames -- and stop only when the SFU no longer
 * hears us. Incoming audio from others is deliberately not used: it also
 * stops when *their* connection dies, and that is not ours to fix.
 *
 * A pure reducer so the timing is testable without a browser.
 */
import { ConnectionState } from "livekit-client";

/** How long media may be absent before the call is brought back by a fresh connection. */
export const STALL_MS = 6000;

export interface StallSample {
  state: ConnectionState;
  /** A published mic whose capture is running (not ended, not interrupted by the OS). */
  micLive: boolean;
  /** Bytes the publisher connection has received so far; null if unknown. */
  feedbackBytes: number | null;
}

export interface StallState {
  /** When LiveKit's own reconnect began; null when not reconnecting. */
  reconnectingSince: number | null;
  /** When the publisher connection last received anything new. */
  progressAt: number | null;
  lastBytes: number | null;
}

export const initialStall = (): StallState => ({
  reconnectingSince: null,
  progressAt: null,
  lastBytes: null,
});

const RECONNECTING = new Set<ConnectionState>([
  ConnectionState.Reconnecting,
  ConnectionState.SignalReconnecting,
]);

export function nextStall(
  prev: StallState,
  sample: StallSample,
  now: number,
): { state: StallState; stalled: boolean } {
  if (RECONNECTING.has(sample.state)) {
    const since = prev.reconnectingSince ?? now;
    return {
      state: { ...initialStall(), reconnectingSince: since },
      stalled: now - since >= STALL_MS,
    };
  }
  if (sample.state !== ConnectionState.Connected) return { state: initialStall(), stalled: false };

  const bytes = sample.feedbackBytes;
  if (!sample.micLive || bytes === null) return { state: initialStall(), stalled: false };
  if (prev.lastBytes === null || prev.progressAt === null || bytes > prev.lastBytes) {
    return { state: { reconnectingSince: null, progressAt: now, lastBytes: bytes }, stalled: false };
  }
  return { state: prev, stalled: now - prev.progressAt >= STALL_MS };
}

/**
 * Bytes received on the publisher connection's selected candidate pair, read
 * from a sender's stats report (which includes its transport and pair).
 */
export function feedbackBytesFrom(report: RTCStatsReport | undefined): number | null {
  if (!report) return null;
  let selectedId: string | undefined;
  report.forEach((s) => {
    if (s.type === "transport" && s.selectedCandidatePairId) selectedId = s.selectedCandidatePairId;
  });
  let bytes: number | null = null;
  report.forEach((s) => {
    if (s.type !== "candidate-pair" || typeof s.bytesReceived !== "number") return;
    const selected = selectedId ? s.id === selectedId : s.nominated && s.state === "succeeded";
    if (selected) bytes = s.bytesReceived;
  });
  return bytes;
}
